import { useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { generatedTypesSxp } from '@css-xp/graphtypes';
import { useNotification, errorNotificationBuilder, NotificationTypes } from '@css-xp/base';
import { NOTIFICATION_DISMISS_TIME } from '../constants/invoices';
import { fetchQuotesByOrderLineId } from '../queries/fetch-quotes-by-order-line-id';

interface Props {
  orderLineIds: string[];
  shouldSkipFetch?: boolean;
  transactionAccountId?: string;
}
export const useQuoteNumberData = ({ orderLineIds, shouldSkipFetch, transactionAccountId }: Props) => {
  const client = useMemo(() => abgGraphqlClient(ProxyGraphClients.SXP), []);

  const { sendErrorNotification, setNotificationSent } = useNotification();

  const { data, loading, error } = useQuery<
    generatedTypesSxp.FetchQuotesByOrderLineIdQuery,
    generatedTypesSxp.FetchQuotesByOrderLineIdQueryVariables
  >(fetchQuotesByOrderLineId, {
    variables: {
      filterOptions: {
        and: [
          {
            key: 'orderLineItemIdIsIn',
            values: orderLineIds,
          },
          { key: 'transactionAccountId', value: transactionAccountId },
        ],
      },
    },
    client,
    skip: shouldSkipFetch || !orderLineIds?.length,
  });

  useEffect(() => {
    // Show error notification if fetch fails
    if (error) {
      const errorNotificationOptions = errorNotificationBuilder()
        .withId('quote-number-fetch-error')
        .withDismissAfter(NOTIFICATION_DISMISS_TIME)
        .isVersion(NotificationTypes.FLAG);

      const options = errorNotificationOptions
        .withType('warning')
        .withHeader('Associated quote number is not available')
        .withMessage('An error occurred while fetching associated quote number. Please try again after some time.')
        .build();

      sendErrorNotification(options);
      setNotificationSent(true);
    }
    // Note: Only error is added as a dependency to avoid infinite loop
  }, [error]);

  return { data, loading };
};
